/**
 * The order chain's Invoice step panel (docs/features/order-to-invoice/
 * 50_finance_sales_ledger_module_spec.md §9 item 2; round 19's
 * `InvoiceStepPanel`): the sales ledger's invoices, then the lines not yet
 * invoiced, with net, VAT and gross on desktop and gross alone on a phone.
 * The money summary and the rules are on the heading's row.
 *
 * Every word is the backend's (views' `order_billing.py`, worded from the
 * ledger). Where the mock-up differed: View PDF is CIS's copy (doc 50 §5.12,
 * doc 70 Q4), with Open in Xero beside it once Xero has the invoice; a held
 * line is not an invoice yet (the ledger numbers one only when it issues), so
 * it sits with the lines not yet invoiced, with its reason and, for finance,
 * Set VAT.
 */
import { Link } from "react-router";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type {
  CreditNoteStateName,
  HeldLine,
  InvoiceStateName,
  OrderView,
} from "@shared-core/data/order-to-invoice-types";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { RailRow, RailRows } from "../../patterns/LifecycleRail";
import { Button } from "../../primitives/button";
import {
  DetailItem,
  PanelHeading,
  ViewDetail,
  nodeOfTone,
  useFocusRef,
} from "./StepPanelParts";

export interface InvoiceStepHandlers {
  busy: boolean;
  /** View PDF: fetch the document's PDF (auth-gated) and save it: an invoice's or a credit note's. */
  onViewPdf: (document: { pdf_url: string }) => void;
  /** Set VAT… on a held line (finance only: the line carries its URL). */
  onSetVat: (line: HeldLine) => void;
}

/** An invoice's state as the rail's node: paid is done, held a problem, the rest moving. */
const STATE_TONE: Record<
  InvoiceStateName,
  "done" | "at_risk" | "late" | "neutral"
> = {
  paid: "done",
  held: "at_risk",
  issued: "neutral",
  draft_in_xero: "neutral",
  awaiting_payment: "neutral",
  voided_in_xero: "late",
};

/** A credit note's state as the rail's node: applied or refunded is done, a failed refund a problem. */
const CREDIT_NOTE_TONE: Record<
  CreditNoteStateName,
  "done" | "at_risk" | "late" | "neutral"
> = {
  applied: "done",
  refunded: "done",
  refund_failed: "late",
  refund_pending: "at_risk",
  draft_in_xero: "neutral",
  approved_in_xero: "neutral",
  issued: "neutral",
  voided_in_xero: "late",
};

/** Net · VAT · gross on desktop; gross alone below md. VAT unresolved reads "+ VAT". */
function Money({
  net,
  vat,
  gross,
}: {
  net: number;
  vat: number | null;
  gross: number | null;
}) {
  if (vat === null || gross === null)
    return (
      <span className="tabular-nums">{formatQuotedPrice(net)} net + VAT</span>
    );
  return (
    <span className="tabular-nums">
      <span className="max-md:hidden">
        {formatQuotedPrice(net)} net · {formatQuotedPrice(vat)} VAT ·{" "}
      </span>
      {formatQuotedPrice(gross)}
      <span className="max-md:hidden"> gross</span>
    </span>
  );
}

export function InvoiceStepPanel({
  order,
  focusLineNo,
  handlers,
}: {
  order: OrderView;
  focusLineNo: string | null;
  handlers: InvoiceStepHandlers;
}) {
  const focusRef = useFocusRef();
  const capabilities = usePlatformCapabilities();
  const billing = order.billing;
  if (!billing) return null;
  let focusedCount = 0;
  return (
    <div className="space-y-2" data-section-id="order.invoice">
      <PanelHeading
        title="Invoices"
        infoLabel="How this order is billed"
        info={billing.rules_sentence}
        summary={billing.summary.sentence}
      />
      <RailRows ariaLabel="Invoices">
        {billing.invoices.map((inv) => {
          const isFocused =
            focusLineNo !== null && inv.line_nos.includes(focusLineNo);
          if (isFocused) focusedCount += 1;
          return (
            <RailRow
              key={inv.invoice_no}
              rowRef={isFocused && focusedCount === 1 ? focusRef : undefined}
              isFocused={isFocused}
              {...nodeOfTone(STATE_TONE[inv.state])}
              label={inv.invoice_no}
              name={`Lines ${inv.line_nos.join(", ")}`}
              status={
                <span className="inline-flex flex-wrap items-center gap-x-1.5">
                  <Money
                    net={inv.net_pence}
                    vat={inv.vat_pence}
                    gross={inv.gross_pence}
                  />
                  <span aria-hidden>·</span>
                  <span>{inv.held_reason ?? inv.state_label}</span>
                  {inv.covers && (
                    <>
                      <span aria-hidden>·</span>
                      <span>{inv.covers}</span>
                    </>
                  )}
                </span>
              }
              actions={
                <span className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={handlers.busy}
                    onClick={() => handlers.onViewPdf(inv)}
                  >
                    View PDF
                  </Button>
                  {inv.xero_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        capabilities.openExternalUrl(inv.xero_url!)
                      }
                    >
                      Open in Xero
                    </Button>
                  )}
                  <Button size="sm" variant="outline" asChild>
                    <Link
                      to={paths.invoiceDetail({ invoiceNo: inv.invoice_no })}
                    >
                      Open
                    </Link>
                  </Button>
                  {inv.raise_credit_note_url && (
                    <Button size="sm" variant="outline" asChild>
                      <Link
                        to={paths.invoiceDetail({
                          invoiceNo: inv.invoice_no,
                          raise: true,
                        })}
                      >
                        Raise credit note…
                      </Link>
                    </Button>
                  )}
                </span>
              }
              detail={
                <ViewDetail title={`Invoice ${inv.invoice_no}`}>
                  <dl className="grid grid-cols-2 gap-x-8 gap-y-3">
                    <DetailItem label="Issued">
                      {formatShortDateOrDash(inv.issued_on)}
                    </DetailItem>
                    <DetailItem label="Due">
                      {formatShortDateOrDash(inv.due_on)}
                    </DetailItem>
                    <DetailItem label="Where it stands" wide>
                      {inv.state_label}
                      {inv.held_reason && `: ${inv.held_reason}`}
                    </DetailItem>
                  </dl>
                  <ul className="divide-y" aria-label="Lines">
                    {inv.lines.map((l) => (
                      <li
                        key={l.line_no}
                        className="flex justify-between gap-4 py-1.5"
                      >
                        <span>
                          {l.line_no} {l.description} ·{" "}
                          {l.quantity.toLocaleString("en-GB")}
                        </span>
                        <span className="tabular-nums">
                          {formatQuotedPrice(l.net_pence)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </ViewDetail>
              }
            />
          );
        })}
        {billing.credit_notes.map((note) => {
          const isFocused =
            focusLineNo !== null && note.line_nos.includes(focusLineNo);
          return (
            <RailRow
              key={note.credit_note_no}
              isFocused={isFocused}
              {...nodeOfTone(CREDIT_NOTE_TONE[note.state])}
              label={`Credit note · ${note.credit_note_no}`}
              name={`Credits ${note.invoice_no} · Lines ${note.line_nos.join(", ")}`}
              status={
                <span className="inline-flex flex-wrap items-center gap-x-1.5">
                  <span className="tabular-nums">
                    {formatQuotedPrice(-note.gross_pence)}
                  </span>
                  <span aria-hidden>·</span>
                  <span>{note.state_label}</span>
                  <span aria-hidden>·</span>
                  <span>{note.refund_label}</span>
                </span>
              }
              actions={
                <span className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={handlers.busy}
                    onClick={() => handlers.onViewPdf(note)}
                  >
                    View PDF
                  </Button>
                  {note.xero_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        capabilities.openExternalUrl(note.xero_url!)
                      }
                    >
                      Open in Xero
                    </Button>
                  )}
                  <Button size="sm" variant="outline" asChild>
                    <Link
                      to={paths.creditNoteDetail({
                        creditNoteNo: note.credit_note_no,
                      })}
                    >
                      Open
                    </Link>
                  </Button>
                </span>
              }
            />
          );
        })}
        {billing.not_yet_invoiced.map((row) => (
          <RailRow
            key={`${row.tone}-${row.label}`}
            state="to_do"
            problem={row.tone === "held" ? "at_risk" : null}
            label={
              row.tone === "not_billable" ? "Not billable" : "Not yet invoiced"
            }
            name={`Lines ${row.line_nos.join(", ")}`}
            status={
              <span className="inline-flex flex-wrap items-center gap-x-1.5">
                <Money
                  net={row.net_pence}
                  vat={row.vat_pence}
                  gross={row.gross_pence}
                />
                <span aria-hidden>·</span>
                <span>{row.label}</span>
              </span>
            }
            expanded={
              row.held_lines.length > 0 ? (
                <ul className="space-y-1 pl-6 text-sm" aria-label="Held lines">
                  {row.held_lines.map((h) => (
                    <li
                      key={h.invoice_line_id}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1"
                    >
                      <span className="tabular-nums text-muted-foreground">
                        {h.line_no}
                      </span>
                      <span>{h.reason}</span>
                      {h.set_vat_treatment_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={handlers.busy}
                          onClick={() => handlers.onSetVat(h)}
                        >
                          Set VAT…
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : undefined
            }
          />
        ))}
      </RailRows>
    </div>
  );
}
